import { inject, Injectable } from '@angular/core';
import { HttpClient, HttpErrorResponse } from '@angular/common/http';
import { Observable, catchError, from, switchMap, throwError } from 'rxjs';
import { environment } from '@environments/environment';
import type {
  PaymentHistoryResponse,
  PaymentHistoryRequest,
  TransactionDetailResponse,
  TransactionDetailRequest,
  ColumnResponse,
  ColumnRequestPayload,
  ExportRequestPayload,
  ExportResponse,
  CounterpartsResponse,
  UpdatePaymentCategoryResponse,
  UpdatePaymentNoteResponse,
  AddPaymentReferenceResponse,
} from '../models';
import { Storage } from '../services/storage';
import { AccountStore } from '../stores/account-store';
import { extendedTimeoutContext } from '../http/request-timeout';

/**
 * TransactionApi - HTTP API calls for transactions
 *
 * Handles all transaction/payment-related HTTP requests:
 * - Payment history retrieval with filters
 * - Transaction details
 * - Receipt downloads
 *
 * Security:
 * - Uses legacy API with static bearer token from environment
 * - Auto-injects merchant credentials from localStorage
 */
@Injectable({
  providedIn: 'root',
})
export class TransactionApi {
  private readonly http = inject(HttpClient);
  private readonly storage = inject(Storage);
  private readonly accountStore = inject(AccountStore);

  /**
   * Get payment history from API with optional filters
   * Automatically retrieves credentials from localStorage
   *
   * @param payload - Complete request payload with filters
   * @returns Observable<PaymentHistoryResponse>
   * @throws Error if user data not found in storage
   */
  getPaymentHistory(payload: Partial<PaymentHistoryRequest>): Observable<PaymentHistoryResponse> {
    const userData = this.storage.getUserData();
    if (!userData) {
      return throwError(() => new Error('User data not found in storage'));
    }

    // Derive the account_id from the currently selected account rather than
    // assuming the first account in the list. Falls back to the default
    // account, then to the first stored account, so requests still resolve a
    // sensible account_id even before the store is fully initialised.
    const accountId = this.accountStore.resolveAccountId();

    // Build complete payload with auth credentials
    const completePayload: PaymentHistoryRequest = {
      merchant_id: userData.merchantId,
      account_id: accountId,
      merchant_user_id: userData.merchantUserId,
      user_type: 'merchant',
      device_type: 'web',
      token: userData.token,
      page: payload.page || 1,
      limit: payload.limit || 20,
      // Every filter travels inside `filters` — see PaymentHistoryRequest.
      ...(payload.filters && { filters: payload.filters }),
    };

    return this.http
      .post<PaymentHistoryResponse>(
        `${environment.apiUrl}/api/gozem_money/payments/history`,
        completePayload,
      )
      .pipe(
        catchError((error: HttpErrorResponse) => {
          return throwError(() => error);
        }),
      );
  }

  /**
   * Get transaction detail by payment ID
   * Returns dynamic UI structure from server
   *
   * @param paymentId - Transaction/payment ID from route
   * @returns Observable<TransactionDetailResponse>
   * @throws Error if user data not found in storage
   */
  getTransactionDetail(paymentId: string): Observable<TransactionDetailResponse> {
    const userData = this.storage.getUserData();
    if (!userData) {
      return throwError(() => new Error('User data not found in storage'));
    }

    const payload: TransactionDetailRequest = {
      payment_id: paymentId,
      merchant_id: userData.merchantId,
      user_type: 'merchant',
      token: userData.token,
      device_type: environment.deviceType,
    };

    return this.http
      .post<TransactionDetailResponse>(
        `${environment.apiUrl}/api/gozem_money/payment_details`,
        payload,
      )
      .pipe(
        catchError((error: HttpErrorResponse) => {
          console.error('Transaction detail API error:', error);
          return throwError(() => error);
        }),
      );
  }

  /**
   * Get dynamic column definitions from server
   * Fetches table column configuration for transaction list
   *
   * @returns Observable<ColumnResponse>
   * @throws Error if user data not found in storage
   */
  getColumns(): Observable<ColumnResponse> {
    const userData = this.storage.getUserData();
    if (!userData) {
      return throwError(() => new Error('User data not found in storage'));
    }

    const payload: ColumnRequestPayload = {
      merchant_id: userData.merchantId,
      merchant_user_id: userData.merchantUserId,
      user_type: 'merchant',
      device_type: 'web',
      token: userData.token,
    };

    return this.http
      .post<ColumnResponse>(`${environment.apiUrl}/api/gozem_money/payments/get_columns`, payload)
      .pipe(
        catchError((error: HttpErrorResponse) => {
          console.error('Column fetch API error:', error);
          return throwError(() => error);
        }),
      );
  }

  /**
   * Get counterparts (clients/tiers) for the merchant
   * Used to populate the "Clients" filter in the transaction filter menu
   *
   * @returns Observable<CounterpartsResponse>
   * @throws Error if user data not found in storage
   */
  getCounterparts(): Observable<CounterpartsResponse> {
    const userData = this.storage.getUserData();
    if (!userData) {
      return throwError(() => new Error('User data not found in storage'));
    }

    return this.http
      .post<CounterpartsResponse>(
        `${environment.apiUrl}/api/gozem_money/payments/get_counterparts`,
        { token: userData.token, merchant_id: userData.merchantId },
      )
      .pipe(
        catchError((error: HttpErrorResponse) => {
          return throwError(() => error);
        }),
      );
  }

  /**
   * Request export of transaction history via email
   * Sends export request with filters and format preferences
   * Uses authInterceptor for automatic token injection
   *
   * @param payload - Export configuration matching backend specification
   * @returns Observable<ExportResponse>
   * @throws HttpErrorResponse - 429 for rate limiting, 400 for validation errors
   */
  requestExport(payload: ExportRequestPayload): Observable<ExportResponse> {
    return this.http
      .post<ExportResponse>(
        `${environment.apiUrl}/api/gozem_money/payments/request_export`,
        payload,
        { context: extendedTimeoutContext() },
      )
      .pipe(
        catchError((error: HttpErrorResponse) => {
          // Handle rate limiting
          if (error.status === 429) {
            console.warn('Export rate limit exceeded:', error);
          }
          return throwError(() => error);
        }),
      );
  }

  /**
   * Update the category of a specific payment
   *
   * @param paymentId - The payment to update
   * @param categoryId - The new category ID
   * @returns Observable<UpdatePaymentCategoryResponse>
   */
  updatePaymentCategory(
    paymentId: string,
    categoryId: string,
  ): Observable<UpdatePaymentCategoryResponse> {
    const userData = this.storage.getUserData();
    if (!userData) {
      return throwError(() => new Error('User data not found in storage'));
    }

    return this.http
      .post<UpdatePaymentCategoryResponse>(
        `${environment.apiUrl}/api/gozem_money/v1/update_payment`,
        {
          payment_id: paymentId,
          merchant_id: userData.merchantId,
          user_type: 'merchant',
          token: userData.token,
          device_type: environment.deviceType,
          category_id: categoryId,
        },
      )
      .pipe(
        catchError((error: HttpErrorResponse) => {
          return throwError(() => error);
        }),
      );
  }

  /**
   * Add a payment reference with an attached file.
   * Converts the file to base64 then POSTs to update_payment.
   *
   * @param paymentId - The payment to attach the reference to
   * @param refNumber - The reference label/name entered by the user
   * @param file - The attached file (JPEG, PNG, or PDF)
   */
  addPaymentReference(
    paymentId: string,
    refNumber: string,
    file: File,
  ): Observable<AddPaymentReferenceResponse> {
    const userData = this.storage.getUserData();
    if (!userData) {
      return throwError(() => new Error('User data not found in storage'));
    }

    const toBase64 = (f: File): Promise<string> =>
      new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = () => {
          const dataUrl = reader.result as string;
          // Strip "data:<mime>;base64," prefix
          resolve(dataUrl.split(',')[1]);
        };
        reader.onerror = () => reject(reader.error);
        reader.readAsDataURL(f);
      });

    const now = new Date();
    const pad = (n: number) => String(n).padStart(2, '0');
    const timestamp =
      `${now.getFullYear()}${pad(now.getMonth() + 1)}${pad(now.getDate())}` +
      `_${pad(now.getHours())}${pad(now.getMinutes())}${pad(now.getSeconds())}`;
    const ext = file.name.includes('.') ? file.name.split('.').pop() : '';
    const filename = `temp_file_${timestamp}${ext ? '.' + ext : ''}`;

    return from(toBase64(file)).pipe(
      switchMap((fileData) =>
        this.http
          .post<AddPaymentReferenceResponse>(
            `${environment.apiUrl}/api/gozem_money/v1/update_payment`,
            {
              payment_id: paymentId,
              merchant_id: userData.merchantId,
              user_type: 'merchant',
              token: userData.token,
              device_type: environment.deviceType,
              payment_references: [
                {
                  file_data: fileData,
                  filename,
                  mimetype: file.type,
                  ref_number: refNumber,
                  size: file.size,
                },
              ],
            },
            { context: extendedTimeoutContext() },
          )
          .pipe(catchError((error: HttpErrorResponse) => throwError(() => error))),
      ),
    );
  }

  /**
   * Update the shared note of a specific payment
   *
   * @param paymentId - The payment to update
   * @param note - The note text to save
   * @returns Observable<UpdatePaymentNoteResponse>
   */
  updatePaymentNote(paymentId: string, note: string): Observable<UpdatePaymentNoteResponse> {
    const userData = this.storage.getUserData();
    if (!userData) {
      return throwError(() => new Error('User data not found in storage'));
    }

    return this.http
      .post<UpdatePaymentNoteResponse>(`${environment.apiUrl}/api/gozem_money/v1/update_payment`, {
        payment_id: paymentId,
        merchant_id: userData.merchantId,
        user_type: 'merchant',
        token: userData.token,
        device_type: environment.deviceType,
        private_note: note,
      })
      .pipe(
        catchError((error: HttpErrorResponse) => {
          return throwError(() => error);
        }),
      );
  }
}
